import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { FormField, ErrorMode, CreateUserRequest, CustomerProfile, MarketingChannel, MarketingContext } from '@/models';
import {
  makeFormField,
  isValidEmail,
  isValidPhoneNumber,
  isValidCustomerName,
  makeFormFieldOfType,
  enforceMaxLength,
  stripNonNumerics,
  formatPhoneNumber,
  isValidBirthday,
  stripLeadingOnesAndZeros,
  validatePassword,
  formatDate,
  parseCountryCode,
  getCountryCodeByTopLevelDomain,
} from '@/helpers';
import { Routes } from '@/router';
import { LoyaltyExplainerModalState } from '@/components/modals/CmgLoyaltyExplainerModal/cmg-loyalty-explainer-modal';
import { AnalyticsEventCategory, AnalyticsEventAction } from '@/services/google/google-analytics.services';
import { CustomerError, CustomerErrorCode } from '@/services/customer/customer.service';
import { CountryCode } from '@/store/ui';
import { persistCrossDomainData, PersistCrossDomainDataOptions, isLoyaltyEnabled } from '@/helpers';
import { AccountManagementEvent, AccountPageLoadEvent, LoginCompleteEvent } from '@/services/analytics/events';
import { GuacModeLinkClickType, LinkClickElementType, PageLoadEventType } from '@/services/analytics/platforms/adobe-analytics/models';
import { GuacModeLinkClickEvent } from '@/services/analytics/events/guac-mode-link-click';
import { countries } from '@/constants'
import { CreateAccountPageLoadEvent } from '@/services/analytics/events/create-account-page-load-event';

export interface AccountCreatorArgs {
  request: CreateUserRequest;
  country: string;
  dateOfBirth: string;
}

interface CreateAccountForm {
  firstName: FormField;
  lastName: FormField;
  email: FormField;
  password: FormField;
  telephone: FormField;
  birthday: FormField;
  receiveTextPromos: FormField<boolean | null>;
  receiveEmailPromos: FormField<boolean | null>;
  enrollInRewards: FormField<boolean | null>;
  country: FormField;
}

const getFormDefaults = (form: CreateAccountForm): CreateAccountForm => {
  form.firstName = makeFormField();
  form.lastName = makeFormField();
  form.email = makeFormField();
  form.password = makeFormField();
  form.telephone = makeFormField();
  form.birthday = makeFormField();
  form.receiveTextPromos = makeFormFieldOfType<boolean | null>(null);
  form.receiveEmailPromos = makeFormFieldOfType<boolean | null>(null);
  form.enrollInRewards = makeFormFieldOfType<boolean | null>(null);
  form.country = makeFormField();

  return form;
};

const createAccountForm: CreateAccountForm = getFormDefaults({} as CreateAccountForm);

@Component({
  name: 'CmgCreateAccountForm',
})
export default class CmgCreateAccountForm extends Vue {
  isLoading = false;
  isFormValid = true;
  accountForm = createAccountForm;
  formSubmissionErrorMessage = '';

  // a flag to toggle animation when swapping country
  showForm = true;
  // a flag to block CSS transition when form is reset after 2sv failure or cancel 
  blockCSSTransition = false;

  @Prop({ default: true, type: Boolean })
  promptSignIn!: boolean;

  @Prop({ default: true, type: Boolean })
  includeCopy!: boolean;

  // Configures if we want the basic (required) fields or the full account creation process
  @Prop({ default: false, type: Boolean })
  requiredFieldsOnly!: boolean;

  @Prop({ default: true, type: Boolean })
  navigationAlert!: boolean;

  @Prop({ type: Object })
  prefill?: CreateUserRequest;

  @Prop({ type: Function })
  accountCreator?: (args: AccountCreatorArgs) => Promise<any>;

  // need to pass this in when this form is rendered for "order confirmation" create account for last restaurant
  //ordered from rather then profile
  @Prop({ required: false, type: String })
  currentCountry?: string;

  get form(): CreateAccountForm {
    return this.accountForm;
  }

  get buttonText(): string {
    return this.$appStore.state.cms.createAccountForm?.buttonText || 'create an account';
  }
  
  get alreadyMemberText() {
    let text = this.$appStore.state.cms.createAccountForm?.text1;
    return text ? text : this.countryCode === 'CA' ? 'have an account?' : 'already a member?'; 
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  // On blur, the telephone number gets formatted, so
  // it requires max length of 14. When user is typing,
  // we want maxLength to be 10.
  get phoneInputMaxLength(): number {
    return this.form.telephone.formatted ? 14 : 10;
  }

  get legalCopy(): string {
    return this.$appStore.state.cms.legalCopy?.createAccount[this.createAccountCountryCode] ?? '';
  }

  get countryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get createAccountCountryCode(): CountryCode {
    return parseCountryCode(this.form.country.value)
  }
  
  get subHeadingText(): string {
    if (this.isVendorCreateAccount) {
      return 'Create an account to participate in all things Chipotle Rewards';
    }
    return isLoyaltyEnabled(this.createAccountCountryCode)
      ? this.$appStore.state.cms.createAccountForm?.subheading || 'Create an account so you can get rewards and order your favorites even faster.'
      : 'Create an account so you can order your favorites even faster.';
  }

  get fromBag(): boolean {
    return this.$appStore.state.adobe.isFromBag;
  }

  get adobeSignInSignUpExperienceA() {
    return this.$appStore.state.ui.signInSignUpVisibilityExperienceA;
  }

  get adobeSignInSignUpExperienceB() {
    return this.$appStore.state.ui.signInSignUpVisibilityExperienceB;
  }

  get selectedCountryLocale() {
    return countries[this.createAccountCountryCode.toLowerCase()].locale;
  }

  get phoneNumberText() {
    return this.form.country.value === CountryCode.CA ? 'For security and order related questions.' : 
            'Enter a valid mobile number that can accept SMS messages. This will be used for two-step verification for account access, and for order-related notifications.';
  }

  get isVendorCreateAccount() {
    return this.$router.currentRoute.name === Routes.VendorCreateAccount;
  }

  // blocks transition by returning empty string if blockCSSTransition is set to true,
  get transitionName(): string {
    return !this.blockCSSTransition ? 'slide-fade' : '';
  }

  @Watch('prefill', { immediate: true })
  onPrefillUpdated(request: CreateUserRequest | undefined) {
    this.form.country.value = this.countryCode;
    if (!request) {
      return;
    }

    this.form.firstName.value = request.firstName || '';
    this.form.lastName.value = request.lastName || '';
    this.form.email.value = request.emailAddress || '';
    this.form.password.value = request.password || '';
    this.form.telephone.value = request.phoneNumber || '';
  }

  mounted() {
    if (this.currentCountry) {
      this.form.country.value = this.currentCountry;
    }

    if (this.$router.currentRoute.name === Routes.GuacModeSignIn) {
      this.$services.analytics.send(new AccountPageLoadEvent(PageLoadEventType.UnlockGuacMode));
    }

    this.$services.analytics.send(new CreateAccountPageLoadEvent());
  }

  validateFirstName(): void {
    if (this.form.firstName.value.length == 0) {
      this.form.firstName.error = 'First Name Required';
      this.form.firstName.errorMode = ErrorMode.PlaceHolder;
    } else if (!isValidCustomerName(this.form.firstName.value)){
      this.form.firstName.error = 'No special characters allowed';
    } else {
      this.form.firstName.error = null;
    }
  }

  validateLastName(): void {
    if (this.form.lastName.value.length == 0) {
      this.form.lastName.error = 'Last Name Required';
    } else if (!isValidCustomerName(this.form.lastName.value)){
      this.form.lastName.error = 'No special characters allowed';
    } else {
      this.form.lastName.error = null;
    }

    this.form.lastName.errorMode = ErrorMode.PlaceHolder;
  }

  validateEmail(): void {
    if (this.form.email.value.length == 0) {
      this.form.email.error = 'Email Required';
    } else {
      this.form.email.error = !isValidEmail(this.form.email.value) ? 'Please Enter A Valid Email' : '';
    }

    this.form.email.errorMode = ErrorMode.PlaceHolder;
  }

  validatePassword(): void {
    validatePassword(this.form.password);
  }

  validateTelephoneNumber(): void {
    if (this.form.telephone.value.length == 0) {
      this.form.telephone.error = 'Phone Number Required';
    } else {
      this.form.telephone.error = !isValidPhoneNumber(this.form.telephone.value) ? 'Please Enter A Valid Phone Number' : '';
    }

    this.form.telephone.errorMode = ErrorMode.PlaceHolder;
  }

  sanitizePhoneNumber(value: string): string {
    return stripLeadingOnesAndZeros(stripNonNumerics(value));
  }

  formatPhoneNumber(value: string): string {
    return formatPhoneNumber(value);
  }

  validateReceiveTextPromos(): void {
    this.form.receiveTextPromos.error = this.form.receiveTextPromos.value === null ? 'Required' : null;
  }

  validateReceiveEmailPromos(): void {
    this.form.receiveEmailPromos.error = this.form.receiveEmailPromos.value === null && this.form.country.value === CountryCode.CA ? 'Required' : null;
  }

  validateEnrollInRewards(): void {
    this.form.enrollInRewards.error = this.form.enrollInRewards.value === null && this.form.country.value === CountryCode.CA ? 'Required' : null;
  }

  validateCountry(): void {
    if (!this.form.country.value) {
      this.form.country.error = 'Please select a country';
    } else if (this.isVendorCreateAccount && this.form.country.value !== CountryCode.US) {
      this.form.country.error = 'This promotion is only available to participants in the United States';
    } else {
      this.form.country.error = '';
    }
  }

  validateBirthday(): void {
    // Validating MMDD
    this.form.birthday.error = !isValidBirthday(this.form.birthday.value) && this.form.birthday.value ? 'Please Enter A Valid Birthday MMDD' : '';

    this.form.birthday.errorMode = ErrorMode.PlaceHolder;
  }

  formatBirthday(value: string): string {
    return formatDate(value);
  }

  enforceMaxLength(value: string, validLengths: number[], formField: FormField): string {
    return enforceMaxLength(value, validLengths, formField);
  }

  stripNonNumerics(value: string): string {
    return stripNonNumerics(value);
  }

  // hide the form when 2sv fails because otherwise the v-form-field directive does not update its bindings
  // and the form fields get in a bad validation state. 
  // Block the CSS transition because hiding and showing the form triggers a transition which isn't necessary on form reset
  hideFormAndBlockCSSTransition(): void {
    this.showForm = false;
    this.blockCSSTransition = true;
  }

  // After resetting the form we want to update the v-form-field directive bindings by showing the form after it has been hidden and reset. 
  // Resume transitions since it may have been blocked by this.hideFormAndBlockCSSTransition()
  showFormAndResumeCSSTransition(): void {
    this.$nextTick(() => {
      this.showForm = true;
      this.$nextTick(() => (this.blockCSSTransition = false));
    });
  }

  proceedAfter2svCancelledOrFailed() {
    this.hideFormAndBlockCSSTransition();
    
    this.resetForm();
    this.form.country.value = getCountryCodeByTopLevelDomain();
    
    this.showFormAndResumeCSSTransition();

    if (this.isVendorCreateAccount) {
      this.$router.push({ name: Routes.VendorSignIn });
    } else {
      this.$services.ui.setModalVisibility('signIn', false);
    }
  }

  resetForm(): void {
    // getFormDefaults will modify the accountForm instance
    // We purposely wanted to keep the same instance in order to make the transition animation work.
    getFormDefaults(this.accountForm);
    this.isFormValid = true;
  }

  validateForm() {
    this.formSubmissionErrorMessage = '';
    this.isFormValid = true;

    this.validateFirstName();
    this.validateLastName();
    this.validateEmail();
    this.validatePassword();
    this.validateTelephoneNumber();
    this.validateReceiveTextPromos();
    this.validateReceiveEmailPromos();
    this.validateEnrollInRewards();
    this.validateCountry();
    this.validateBirthday();

    for (const key of Object.keys(this.form) as Array<keyof CreateAccountForm>) {
      if (!!this.form[key].error) {
        this.form[key].dirty = true;
        this.isFormValid = false;
      }
    }
  }

  onClickReceiveTextPromos(value: boolean) {
    this.form.receiveTextPromos.error = '';
    this.form.receiveTextPromos.dirty = true;
    this.form.receiveTextPromos.value = value;
  }

  onClickReceiveEmailPromos(value: boolean) {
    this.form.receiveEmailPromos.error = '';
    this.form.receiveEmailPromos.dirty = true;
    this.form.receiveEmailPromos.value = value;
  }

  onClickEnrollInRewards(value: boolean) {
    this.form.enrollInRewards.error = '';
    this.form.enrollInRewards.dirty = true;
    this.form.enrollInRewards.value = value;
  }

  onSelectCountry(value: string): void {
    if (this.form.country.value != value) {
      this.form.country.error = '';
      this.form.country.dirty = true;
      this.form.country.value = value;

      this.showForm = false;
      this.$nextTick(() => (this.showForm = true));

      this.$emit(
        'country-selected',
        value,
        {
          emailAddress: this.form.email.value,
          firstName: this.form.firstName.value,
          lastName: this.form.lastName.value,
          password: this.form.password.value,
          phoneNumber: this.form.telephone.value,
        },
        (country: string) => {
          this.onSelectCountry(country);
        },
      );
    }
  }

  async onCreateAccount() {
    this.isLoading = true;
    this.validateForm();

    if (!this.isFormValid) {
      this.isLoading = false;
      return;
    }

    const request = {
      emailAddress: this.form.email.value,
      firstName: this.form.firstName.value,
      lastName: this.form.lastName.value,
      password: this.form.password.value,
      phoneNumber: this.form.telephone.value,
      isAnonymous: false,
      isGuest: false,
      country: this.form.country.value,
      enrollInLoyalty: this.form.country.value === CountryCode.CA ? this.form.enrollInRewards.value! : true,
      enrollInEmailMarketing: this.form.country.value === CountryCode.CA ? this.form.receiveEmailPromos.value! : true,
      enrollInSmsMarketing: this.form.receiveTextPromos.value ?? false
    };

    if (this.accountCreator) {
      try {
        await this.accountCreator({ request, country: this.form.country.value, dateOfBirth: this.formatBirthdayString() });
        this.isLoading = false;
      } catch (error) {
        if (error instanceof CustomerError) {
          this.formSubmissionErrorMessage = error.message; 
        } else {
          this.$services.ui.setModalVisibility('globalErrorToast', true);
        }
        this.isLoading = false;
        return;
      }
    } else {
      try {
        const result = await this.createAccount(request);
        this.isLoading = false;

        if (result == CustomerErrorCode.TwoStepVerificationRequired || result == CustomerErrorCode.AccountVerificationRequired) { // Create account throws Gigya 2 step error code
          
          this.$services.ui.setModalVisibility('verifyMobileNumber', true, { 
            email: this.form.email.value, 
            phoneNumber: this.form.telephone.value,
            requiresPasswordVerification: false,
            requiresSignOutOnFailure: true,
            onSuccess: () => this.proceedAfterAccountCreation(),
            onFailure: () => {
              this.proceedAfter2svCancelledOrFailed();
            }
          });
          this.$services.ui.firstFocus();
        } else {
          // In current design, we should never get here, but if we do, I guess register user account when through successfully
          // No need for 2SV because Gigya wont be expecting it
          this.proceedAfterAccountCreation();
        }
      } catch (e) {
        return;
      }
    }
  }

  async proceedAfterAccountCreation(): Promise<void> {
    const sendAnalytics = !this.isVendorCreateAccount;
    
    try {
      if (sendAnalytics) {
        this.$services.googleAnalytics.sendCustomEvent({
          category: AnalyticsEventCategory.Customer,
          action: AnalyticsEventAction.CreateAccountSuccess,
          label: document.title,
        });
  
        this.$services.branch.createAccountSuccessEvent();
      }
  
      if (!this.requiredFieldsOnly) {
        try {
          const birthday = this.form.birthday.value;
  
          await this.$services.customer.updateCustomer({
            country: this.form.country.value,
            dateOfBirth: !!birthday ? `${birthday.slice(0, 2)}-${birthday.slice(2)}-1972` : '', // TODO-ppcjz2: Revisit this nonsense. User doesn't put in year.
            firstName: this.form.firstName.value,
            lastName: this.form.lastName.value,
            phoneNumber: this.form.telephone.value,
          });
  
          await this.createAccountSubscriptions(sendAnalytics);
        } catch (e) {
          this.$services.logger.error('TODO-ahhucv Error handling for update customer: ', e);
          this.isLoading = false;
        }
      }

      this.persistCrossDomainData();
      this.sendAdobeTargetEvent();
      this.isLoading = false;
    } catch (e) {
      return;
    }

    await this.finalizeLoyaltyEnrollment();
  }

  async finalizeLoyaltyEnrollment() {
    try {
      // fetch user info
      await this.$services.customer.refreshCustomerProfileInfo();
    } catch (e) {
      this.$services.logger.error('Something went wrong refreshing customer info: ', undefined, e);
      return;
    }

    this.isLoading = false;
    this.postLoyaltyIdToParentFrame();

    // assign this route arg here because it will get unset when this modal gets closed
    const toRouteName = this.$appStore.state.ui.modals.signIn.args?.route ?? null;
    this.$services.ui.setModalVisibility('signIn', false);
    this.$services.ui.setModalVisibility('guacModeSignIn', false);

    this.$services.analytics.send(new AccountManagementEvent('registrationComplete'));
    this.$services.analytics.addRegistrationCompleteEventToStore({'value': 1});
    this.$services.analytics.send(new LoginCompleteEvent());

    if (this.$router.currentRoute.name === Routes.GuacModeSignIn) {
      this.$services.analytics.send(new GuacModeLinkClickEvent(GuacModeLinkClickType.CreateAnAccount, LinkClickElementType.Button));
    }

    this.showLoyaltyModal(toRouteName);
    this.navigateAway(toRouteName);
  }

  onSignIn() {
    this.resetForm();
    if (this.$router.currentRoute.name == Routes.CreateAccount) {
      this.$router.push({ name: Routes.SignIn });
    } else if (this.isVendorCreateAccount) {
      this.$router.push({ name: Routes.VendorSignIn });
    }  else if (this.$router.currentRoute.name === Routes.GuacModeSignIn) {
      this.$services.ui.setModalVisibility('guacModeSignIn', true);
      this.$services.analytics.send(new GuacModeLinkClickEvent(GuacModeLinkClickType.SignIn, LinkClickElementType.Link));
      this.$services.analytics.send(new AccountPageLoadEvent(PageLoadEventType.GuacModeNewAccount));
    } else {
      const route = this.$appStore.state.ui.modals.signIn.args ? this.$appStore.state.ui.modals.signIn.args.route : null;
      this.$services.ui.setModalVisibility('signIn', true, { route });
      this.$services.ui.firstFocus();
    }
    this.$emit('country-selected', this.countryCode);
  }

  async createAccount(request: CreateUserRequest): Promise<any> {
    try {
      return await this.$services.customer.registerUser(request);
    } catch (error) {
      if (error instanceof CustomerError) {
        this.formSubmissionErrorMessage = error.message;
      } else {
        this.$services.ui.setModalVisibility('globalErrorToast', true);
      }
      this.isLoading = false;
      throw error;
    }
  }

  private async createAccountSubscriptions(sendAnalytics: boolean): Promise<void> {
    const shouldSubscribeToMarketingSMS = this.form.receiveTextPromos.value ?? false;
    const shouldSubscribeToMarketingEmail = this.form.country.value === CountryCode.CA ? this.form.receiveEmailPromos.value! : true;
    const shouldEnrollInLoyalty = this.form.country.value === CountryCode.CA ? this.form.enrollInRewards.value! : true;
    if (shouldEnrollInLoyalty) {
      try {
        await this.$services.loyalty.enrollInLoyalty({
          customer: this.customer,
          pageTitle: document.title,
          shouldSubscribeToMarketingSMS,
          shouldSubscribeToMarketingEmail,
        });
      } catch (err) {
        this.$services.logger.error('Unable to enroll in loyalty', undefined, err);
        return;
      }
      sendAnalytics &&
        this.$services.googleAnalytics.sendCustomEvent({
          category: AnalyticsEventCategory.Rewards,
          action: AnalyticsEventAction.EnrollSuccess,
          label: document.title,
        });
    } else {
      const subscribeTo = this.$services.customer.subscribeToMarketingChannel.bind(this.$services.customer);
      const results = await Promise.allSettled([
        ...(shouldSubscribeToMarketingSMS ? [subscribeTo(MarketingChannel.Sms, MarketingContext.Marketing)] : []),
        ...(shouldSubscribeToMarketingEmail ? [subscribeTo(MarketingChannel.Email, MarketingContext.Marketing)] : []),
      ]);
      results.forEach(result => {
        if (result.status === 'rejected') {
          this.$services.logger.error('Unable to subscribe to marketing channel', undefined, result.reason);
        }
      });
    }
  }

  private persistCrossDomainData(): void {
    if (this.isVendorCreateAccount) {
      return;
    }
    const dataToPersistCrossDomain: PersistCrossDomainDataOptions = {
      data: {
        deliveryAddress: this.$appStore.state.order.selectedDeliveryAddress,
        order: this.$services.order.getCurrentOrder(),
        jwt: this.$appStore.state.customer.jwt,
      },
      sessionFrameUrl: this.$services.config.getSessionFrameUrl(),
      restaurant: this.$appStore.state.restaurant.selectedRestaurant,
      shouldRedirect: false,
      redirectQueryParams: {},
    };
    persistCrossDomainData(dataToPersistCrossDomain);
  }

  private postLoyaltyIdToParentFrame(): void {
    if (this.isVendorCreateAccount) {
      this.$services.loyalty.postLoyaltyIdToParentFrame();
    }
  }

  private showLoyaltyModal(toRouteName: Routes | null): void {
    const shouldNotShow = toRouteName === Routes.GroupOrderCreate || this.isVendorCreateAccount;
    if (shouldNotShow) {
      return;
    }
    const { profile } = this.$appStore.state.customer;
    if (!isLoyaltyEnabled(profile?.country || '')) {
      this.$services.ui.setModalVisibility('noLoyaltyForYou', true);
    } else if (this.$services.loyalty.isEnrolledInLoyalty(profile)) {
      this.$services.ui.showOnboardingModal(false, this.$services.customer, this.customer ?? undefined);
    } else {
      this.$services.ui.setModalVisibility('loyaltyExplainer', true, {
        state: LoyaltyExplainerModalState.WelcomeUnenrolled,
      });
      this.$services.ui.firstFocus();
    }
  }

  private navigateAway(toRouteName: Routes | null): void {
    const shouldStay = [Routes.OrderConfirmation, Routes.VendorCreateAccount].includes(this.$router.currentRoute.name as Routes);
    if (shouldStay) {
      return;
    }
    this.$router.push({ name: toRouteName ?? Routes.Home });
  }

  private sendAdobeTargetEvent() {
    if (this.isVendorCreateAccount) {
      return;
    }

    // Signin-Signup experince logic
    if (((this.adobeSignInSignUpExperienceA || this.adobeSignInSignUpExperienceB) && this.fromBag)) {
      this.$services.utils.targetEvent('createAccountFromBagSuccess');
      this.$services.utils.setIsFromBag(false);
    }
  }

  private formatBirthdayString(): string {
    const birthday = this.form.birthday.value;

    return !!birthday ? `${birthday.slice(0, 2)}-${birthday.slice(2)}-1972` : ''; // TODO-ppcjz2: Revisit this nonsense. User doesn't put in year.
  }
}
